
import { Vue, Component, Prop } from "vue-property-decorator";
import { AmCard, AmCardBody } from "@afe/amui/src/components/Card";
import { AmTypography } from "@afe/amui/src/components/Typography";
import { AmButton } from "@afe/amui/src/components/Button";
import { AmSpacing } from "@afe/amui/src/components/Spacing";
import { required } from "vuelidate/lib/validators";

import {
  FormField,
  TextField,
  Preloader,
  FormError,
  FormSubmit
} from "../components";
import { loginFlow, LOGIN_STEPS, LoginSubmitForm, verifySms } from "../service";
import { makeAnalytics } from "../analytics";
import get from "lodash/get";

@Component({
  validations: {
    otp: {
      required
    }
  },
  components: {
    AmCard,
    AmTypography,
    AmCardBody,
    AmSpacing,
    AmButton,
    FormField,
    TextField,
    Preloader,
    FormError,
    FormSubmit
  }
})
export default class extends Vue {
  @Prop(Object) readonly config!: any;
  @Prop(Object) readonly formData!: any;
  @Prop({ default: () => loginFlow }) readonly submitForm!: LoginSubmitForm;

  otp = "";
  isError = false;
  errorCode = "";
  loading = false;
  loginUrl = "";
  isNewDashboard = false;
  analytics = makeAnalytics();

  timer = 0;
  delayCounter = 0;
  disableResend = false;
  newDeviceSms = false;

  beforeDestroy() {
    clearInterval(this.timer);
  }

  startSmsTimer(delay: number) {
    this.delayCounter = delay;
    this.timer = window.setInterval(this.countDown.bind(this), 1000);
  }

  async sendSmsWithDelay() {
    this.disableResend = true;
    try {
      await this.submitForm({
        email: this.formData.email,
        phone_number: this.formData.phone_number,
        password: this.formData.password
      });
    } catch ({ throttleDelay }) {
      this.startSmsTimer(throttleDelay);
    }
    this.disableResend = false;
  }

  countDown() {
    if (this.delayCounter <= 0) {
      clearInterval(this.timer);
      this.delayCounter = 0;

      return;
    }

    this.delayCounter--;
  }

  onPrevStep() {
    this.$emit("prevStep");
  }

  onSubmit() {
    this.$v.$touch();
    if (this.$v.$invalid) {
      return;
    }

    this.loading = true;
    this.isError = false;
    const {
      email,
      password,
      company_id,
      phone_number,
      entity,
      entity_type
    } = this.formData;

    this.submitForm({
      email,
      password,
      company_id,
      phone_number,
      otp: this.otp,
      otp_type: this.otpType,
      entity,
      entity_type
    })
      .then(({ next, data }) => {
        if (next === LOGIN_STEPS.REDIRECT_STEP) {
          this.analytics.gtm.eventCall("UserID", {
            UserID: (data && data.user_id) || ""
          });
          this.analytics.gtm.eventCall("Login Success", {
            eventTimeout: 2000,
            eventCallback: () => {
              setTimeout(() => {
                this.$emit("redirect", data);
              }, 1000);
            }
          });
        }
      })
      .catch(error => {
        this.errorCode = get(
          error,
          ["errors", "code"],
          "authentication_failed_2fa"
        );
        this.isError = true;
        this.loading = false;
      });
  }

  onResendCode() {
    this.loading = true;
    this.isError = false;
    this.otp = "";
    const { email, password, phone_number } = this.formData;

    this.submitForm({
      email,
      password,
      phone_number
    }).finally(() => {
      this.loading = false;
      this.isError = false;
    });
  }

  onVerifySms() {
    this.newDeviceSms = true;
    this.otp = "";
    verifySms(this.formData.otp.otp_code_id);
  }

  get otpHeader() {
    return this.otpType === "email"
      ? this.config.otp.newDeviceHeader
      : this.config.otp.header;
  }

  get otpText() {
    if (this.otpType === "sms_auth") {
      return `${this.config.otp.subHeaderSms} ${this.formData.otp.otp_description}`;
    }

    if (this.otpType === "gauth") {
      return this.config.otp.subHeaderGauth;
    }

    return this.config.otp.subHeaderEmail;
  }

  get otpType() {
    return this.formData.otp.otp_types[0];
  }

  get isSmsActive() {
    return (
      !!this.formData.otp.otp_description && !!this.formData.otp.otp_code_id
    );
  }

  get isNewDevice() {
    return this.otpType === "email";
  }

  get isSmsType() {
    return this.otpType === "sms_auth";
  }

  get isWaiting() {
    return this.delayCounter > 0;
  }

  get disableSmsSend() {
    return this.otp.length !== 6;
  }
}
